import { useEffect, useMemo, useState } from "react";
import { api, inr, inrMoney, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import {
  Building2, Plus, Settings2, Users, ShoppingBag, BadgeIndianRupee,
  ExternalLink, GitFork, Loader2, LogOut, Search,
  School2, X, Check, Activity, Phone, Mail, Receipt,
  FileText, Copy, ScanLine, MonitorSmartphone, Settings, Clock,
} from "lucide-react";
import { toast } from "sonner";

/** Platform / super-admin console — manages all schools (tenants).
 * Usable only by users with role = "super_admin".
 */
export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [schools, setSchools] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [detail, setDetail] = useState(null);
  const [invoicesFor, setInvoicesFor] = useState(null);
  const [inviting, setInviting] = useState(null); // { school }

  const load = async () => {
    setLoading(true);
    try {
      const [sl, st] = await Promise.all([
        api.get("/platform/schools"),
        api.get("/platform/stats"),
      ]);
      setSchools(sl.data || []);
      setStats(st.data || null);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return schools;
    return schools.filter(s => `${s.name} ${s.slug} ${s.address || ""}`.toLowerCase().includes(q));
  }, [schools, search]);

  return (
    <div className="min-h-screen bg-cream warm-grain">
      <header className="sticky top-0 z-30 bg-cream/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo size="sm" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] bg-brand text-white px-2 py-0.5 rounded-full">PLATFORM</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-ink-700">{user?.name}</span>
            <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }}
              className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-brand">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand">Platform console</div>
            <h1 className="font-heading text-4xl font-extrabold text-ink mt-2">All schools</h1>
            <p className="text-sm text-ink-500 mt-1">Create tenants, invite admins, peek into stats, and meter billing across the network.</p>
          </div>
          <button data-testid="new-school-btn" onClick={() => setCreatingOpen(true)}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand text-white font-semibold hover:bg-brand-700 shadow-soft">
            <Plus size={16} /> New school
          </button>
        </div>

        {/* Cross-school KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="cross-stats">
          <Stat testid="kpi-tenants" icon={School2} label="Tenants" value={stats?.total_schools ?? "—"} sub={`${stats?.active_schools ?? 0} active`} />
          <Stat testid="kpi-users" icon={Users} label="Users" value={stats?.total_users ?? "—"} />
          <Stat testid="kpi-orders" icon={ShoppingBag} label="Orders (all-time)" value={stats?.total_orders ?? "—"} />
          <Stat testid="kpi-revenue" icon={BadgeIndianRupee} label="Revenue (30d)" value={stats?.total_revenue_30d != null ? inrMoney(stats.total_revenue_30d) : "—"} />
        </div>

        {/* Search */}
        <div className="mt-6 flex items-center gap-2 max-w-md">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input data-testid="school-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, slug, or address…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-border bg-white text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-ink-500"><Loader2 size={20} className="animate-spin inline mr-2"/> Loading…</div>
        ) : (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(s => (
              <SchoolCard key={s.id} s={s}
                onDetail={async () => {
                  try {
                    const { data } = await api.get(`/platform/schools/${s.slug}`);
                    setDetail(data);
                  } catch (e) { toast.error(formatErr(e)); }
                }}
                onInvite={() => setInviting({ school: s })}
                onInvoices={() => setInvoicesFor(s)}
                onBilling={async () => {
                  try {
                    const { data } = await api.get(`/platform/schools/${s.slug}/billing`);
                    setBilling(data);
                  } catch (e) { toast.error(formatErr(e)); }
                }}
              />
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full bg-white rounded-3xl border border-border p-10 text-center text-ink-500">
                {search ? "No matches." : "No schools yet — create one to get started."}
              </div>
            )}
          </div>
        )}
      </main>

      {creatingOpen && <CreateSchoolModal onClose={() => setCreatingOpen(false)} onCreated={() => { setCreatingOpen(false); load(); }} />}
      {detail && <SchoolDetailDrawer school={detail} onClose={() => setDetail(null)} onSaved={() => { setDetail(null); load(); }} />}
      {inviting && <InviteAdminModal school={inviting.school} onClose={() => setInviting(null)} onInvited={() => { setInviting(null); load(); }} />}
      {billing && <BillingModal billing={billing} onClose={() => setBilling(null)} />}
      {invoicesFor && <InvoicesDrawer school={invoicesFor} onClose={() => setInvoicesFor(null)} />}
    </div>
  );
}

// -------------------------------------------------
function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-500"><Icon size={12}/> {label}</div>
      <div className="font-heading text-3xl font-extrabold text-ink mt-1">{value}</div>
      {sub && <div className="text-[11px] text-ink-500 mt-1">{sub}</div>}
    </div>
  );
}

function SchoolCard({ s, onDetail, onInvite, onBilling, onInvoices }) {
  const status = s.status || "active";
  const tone = status === "active" ? "bg-emerald-100 text-emerald-900" : status === "suspended" ? "bg-amber-100 text-amber-900" : "bg-ink-100 text-ink-700";
  return (
    <div className="bg-white rounded-3xl border border-border p-5 hover:shadow-soft transition-shadow" data-testid={`school-card-${s.slug}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading text-xl font-extrabold text-ink truncate">{s.name}</div>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${tone}`}>{status}</span>
          </div>
          <div className="text-[11px] text-ink-500 font-mono mt-0.5">/{s.slug} · {s.slug}hub.grubpe.com</div>
          {s.address && <div className="text-xs text-ink-500 mt-1 truncate">{s.address}</div>}
        </div>
        <Building2 size={22} className="text-ink-400 flex-shrink-0" />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4">
        <Mini label="Orders" value={s.stats?.orders_total ?? 0} />
        <Mini label="Users" value={s.stats?.users ?? 0} />
        <Mini label="Rev (30d)" value={inrMoney(s.stats?.revenue_30d || 0)} />
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <button data-testid={`detail-${s.slug}`} onClick={onDetail} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-ink text-white text-xs font-bold hover:opacity-90"><Settings2 size={12}/> Details</button>
        <button data-testid={`invite-${s.slug}`} onClick={onInvite} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-cream-200 text-ink-800 text-xs font-bold hover:bg-cream-300"><Users size={12}/> Invite admin</button>
        <button data-testid={`billing-${s.slug}`} onClick={onBilling} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-100 text-amber-900 text-xs font-bold hover:bg-amber-200"><Activity size={12}/> Quick stats</button>
        <button data-testid={`invoices-${s.slug}`} onClick={onInvoices} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-purple-100 text-purple-900 text-xs font-bold hover:bg-purple-200"><Receipt size={12}/> Invoices</button>
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-cream-100 rounded-xl p-2 text-center">
      <div className="text-[9px] uppercase tracking-wider text-ink-500 font-bold">{label}</div>
      <div className="font-heading text-base font-extrabold text-ink mt-0.5">{value}</div>
    </div>
  );
}

// -------------------------------------------------
function CreateSchoolModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", slug: "", address: "", phone: "", email: "",
    operation_mode: "school", clone_template: true,
    initial_admin_email: "", initial_admin_name: "",
  });
  const [busy, setBusy] = useState(false);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 16);

  const submit = async () => {
    if (!form.name.trim() || !form.slug.trim()) return toast.error("Name and slug required");
    setBusy(true);
    try {
      const body = {
        name: form.name, slug: form.slug, address: form.address,
        phone: form.phone, email: form.email,
        operation_mode: form.operation_mode, clone_template: form.clone_template,
        initial_admin: form.initial_admin_email
          ? { email: form.initial_admin_email, name: form.initial_admin_name || "School Admin" }
          : null,
      };
      const { data } = await api.post("/platform/schools", body);
      toast.success(`Created ${data.school.name} · cloned ${data.cloned.menu_items} menu items, ${data.cloned.vendors} vendors`);
      onCreated();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="New school" testid="create-school-modal">
      <div className="space-y-3">
        <Field label="School name *">
          <input data-testid="cs-name" autoFocus value={form.name}
            onChange={e => { const n = e.target.value; setForm(f => ({ ...f, name: n, slug: f.slug || autoSlug(n) })); }}
            placeholder="DAV Public School"
            className="input" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Slug *" hint="Lowercase, no spaces. Used for subdomain.">
            <input data-testid="cs-slug" value={form.slug} onChange={e => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9]/g,""))} placeholder="dav" className="input font-mono" />
          </Field>
          <Field label="Operation mode">
            <select data-testid="cs-mode" value={form.operation_mode} onChange={e => setField("operation_mode", e.target.value)} className="input">
              <option value="school">School-operated</option>
              <option value="vendor">Vendor-operated</option>
            </select>
          </Field>
        </div>
        <Field label="Address"><input data-testid="cs-address" value={form.address} onChange={e => setField("address", e.target.value)} className="input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone"><input data-testid="cs-phone" value={form.phone} onChange={e => setField("phone", e.target.value)} className="input" /></Field>
          <Field label="Public email"><input data-testid="cs-email" value={form.email} onChange={e => setField("email", e.target.value)} className="input" /></Field>
        </div>

        <div className="bg-cream-100 rounded-2xl p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input data-testid="cs-clone" type="checkbox" checked={form.clone_template} onChange={e => setField("clone_template", e.target.checked)} className="mt-1" />
            <div>
              <div className="font-bold text-sm flex items-center gap-1"><GitFork size={12}/> Clone the SMI template</div>
              <div className="text-xs text-ink-500 mt-0.5">Copies 12 menu items, 3 meal plans, 4 subsidy tiers, and 3 vendor stalls. Admin can tweak after.</div>
            </div>
          </label>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-amber-900 mb-2">Initial school admin (optional)</div>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="cs-admin-name" placeholder="Admin name" value={form.initial_admin_name} onChange={e => setField("initial_admin_name", e.target.value)} className="input" />
            <input data-testid="cs-admin-email" placeholder="admin@dav.com" value={form.initial_admin_email} onChange={e => setField("initial_admin_email", e.target.value)} className="input" />
          </div>
          <div className="text-[10px] text-amber-800 mt-1">Default password is grubpe123 — admin should rotate on first login.</div>
        </div>
      </div>
      <ModalActions onClose={onClose} onSubmit={submit} busy={busy} submitLabel="Create school" testid="cs-submit" />
    </Modal>
  );
}

function SchoolDetailDrawer({ school, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: school.name || "", address: school.address || "", phone: school.phone || "",
    email: school.email || "", logo_url: school.logo_url || "",
    operation_mode: school.operation_mode || "school",
    vertical_type: school.vertical_type || "school",
    operating_mode: school.operating_mode || "subscription_preorder",
    instashop_enabled: school.instashop_enabled || false,
    meal_plan_enabled: school.meal_plan_enabled !== false,
    preorder_enabled: school.preorder_enabled !== false,
    instashop_operator: school.instashop_operator || "school",
    status: school.status || "active",
    principal_name: school.principal_name || "", principal_phone: school.principal_phone || "", principal_email: school.principal_email || "",
    admin_contact_name: school.admin_contact_name || "", admin_contact_phone: school.admin_contact_phone || "", admin_contact_email: school.admin_contact_email || "",
    escalation_contacts: Array.isArray(school.escalation_contacts) && school.escalation_contacts.length ? school.escalation_contacts : [{ name: "", role: "", phone: "", email: "" }],
    gstin: school.gstin || "",
    billing_model: school.billing_model || { type: "fixed", rate: 0, base_monthly: 500, notes: "" },
    slot_timings: school.slot_timings || { breakfast: "07:30-09:00", lunch: "12:00-14:00", snacks: "15:30-17:00" },
    terms: Array.isArray(school.terms) && school.terms.length ? school.terms : ["Term I", "Term II", "Term III"],
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setBM = (k, v) => setForm(f => ({ ...f, billing_model: { ...(f.billing_model || {}), [k]: v } }));
  const setEsc = (idx, k, v) => setForm(f => ({ ...f, escalation_contacts: f.escalation_contacts.map((c, i) => i === idx ? { ...c, [k]: v } : c) }));
  const addEsc = () => setForm(f => ({ ...f, escalation_contacts: [...(f.escalation_contacts || []), { name: "", role: "", phone: "", email: "" }] }));
  const rmEsc = (idx) => setForm(f => ({ ...f, escalation_contacts: f.escalation_contacts.filter((_, i) => i !== idx) }));

  const links = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return {
      hub: `${origin}/login?school=${school.slug}&next=/admin`,
      qr: `${origin}/qr?school=${school.slug}`,
      pos: `${origin}/login?school=${school.slug}&next=/pos`,
      kds: `${origin}/login?school=${school.slug}&next=/kds`,
      kiosk: `${origin}/kiosk?school=${school.slug}`,
    };
  }, [school.slug]);

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copied"); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      // Strip empty escalation rows
      payload.escalation_contacts = (payload.escalation_contacts || []).filter(c => (c.name || c.email || c.phone));
      // Strip empty term names
      payload.terms = (payload.terms || []).map(t => (t || "").trim()).filter(Boolean);
      // Coerce billing rate/base into numbers
      payload.billing_model = {
        ...payload.billing_model,
        rate: Number(payload.billing_model.rate) || 0,
        base_monthly: Number(payload.billing_model.base_monthly) || 0,
      };
      await api.put(`/platform/schools/${school.slug}`, payload);
      toast.success("School updated"); onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-cream w-full max-w-2xl ml-auto h-full flex flex-col" onClick={e => e.stopPropagation()} data-testid="school-detail-drawer">
        <div className="flex-shrink-0 bg-white border-b border-border px-6 py-4 flex items-start justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand">Tenant detail</div>
            <div className="font-heading text-2xl font-extrabold text-ink mt-1">{school.name}</div>
            <div className="text-[11px] text-ink-500 font-mono mt-0.5">/{school.slug}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-cream-200 inline-flex items-center justify-center"><X size={16}/></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Quick links */}
          <Section title="Quick links" icon={ExternalLink}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <LinkRow label="Web Hub (admin)" url={links.hub} icon={Building2} testid="link-hub" onCopy={copy} />
              <LinkRow label="QR ordering" url={links.qr} icon={ScanLine} testid="link-qr" onCopy={copy} />
              <LinkRow label="POS terminal" url={links.pos} icon={MonitorSmartphone} testid="link-pos" onCopy={copy} />
              <LinkRow label="Kitchen display" url={links.kds} icon={MonitorSmartphone} testid="link-kds" onCopy={copy} />
              <LinkRow label="Self-service kiosk" url={links.kiosk} icon={ScanLine} testid="link-kiosk" onCopy={copy} />
              <LinkRow label="EdKonnect SSO config" url={`${origin}/admin/edkonnect`} icon={GitFork} testid="link-edkonnect" onCopy={copy} />
            </div>
            <div className="text-[10px] text-ink-500 mt-2">Production subdomains will be <span className="font-mono">{school.slug}hub.grubpe.com / {school.slug}qr.grubpe.com / {school.slug}pos.grubpe.com</span> once DNS is configured.</div>
          </Section>

          {/* General */}
          <Section title="General" icon={Building2}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name"><input data-testid="sd-name" value={form.name} onChange={e => set("name", e.target.value)} className="input" /></Field>
              <Field label="Slug" hint="Locked"><input value={school.slug} disabled className="input font-mono opacity-60 cursor-not-allowed" /></Field>
            </div>
            <Field label="Address"><input value={form.address} onChange={e => set("address", e.target.value)} className="input" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Vertical type">
                <select value={form.vertical_type} onChange={e => set("vertical_type", e.target.value)} className="input">
                  <option value="school">School (Canteen)</option>
                  <option value="college">College / University (Food court)</option>
                  <option value="corporate">Corporate (Food court)</option>
                  <option value="mall">Mall (Food court)</option>
                </select>
              </Field>
              <Field label="Operation mode" hint="Default for new counters">
                <select value={form.operation_mode} onChange={e => set("operation_mode", e.target.value)} className="input">
                  <option value="school">School-operated</option>
                  <option value="vendor">Vendor-operated</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Public phone"><input value={form.phone} onChange={e => set("phone", e.target.value)} className="input" /></Field>
              <Field label="Public email"><input value={form.email} onChange={e => set("email", e.target.value)} className="input" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="School GSTIN" hint="Used when school-operated counter issues an invoice">
                <input data-testid="sd-gstin" value={form.gstin} onChange={e => set("gstin", e.target.value.toUpperCase())} placeholder="36AAACS0000Z1ZS" className="input font-mono" />
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={e => set("status", e.target.value)} className="input">
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="archived">Archived</option>
                </select>
              </Field>
            </div>
          </Section>

          {/* Service Configuration */}
          <Section title="Service configuration" icon={Settings}>
            <div className="text-xs text-ink-500 mb-3">Enable the services available for this school. Meal Plan and Pre-ordering are always school-operated.</div>
            
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-border hover:border-brand/30 transition-colors">
                <input type="checkbox" data-testid="sd-meal-plan" checked={!!form.meal_plan_enabled}
                  onChange={e => set("meal_plan_enabled", e.target.checked)}
                  className="w-5 h-5 rounded border-border text-brand focus:ring-brand mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-ink">Meal Plan (Subscription)</div>
                  <div className="text-xs text-ink-500">Monthly/quarterly subscriptions. RFID tap-to-eat. School-operated.</div>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-border hover:border-brand/30 transition-colors">
                <input type="checkbox" data-testid="sd-preorder" checked={!!form.preorder_enabled}
                  onChange={e => set("preorder_enabled", e.target.checked)}
                  className="w-5 h-5 rounded border-border text-brand focus:ring-brand mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-ink">Pre-ordering</div>
                  <div className="text-xs text-ink-500">Parents order daily meals for specific dates. School-operated.</div>
                </div>
              </label>

              <div className="p-3 rounded-xl border border-border">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" data-testid="sd-instashop" checked={!!form.instashop_enabled}
                    onChange={e => set("instashop_enabled", e.target.checked)}
                    className="w-5 h-5 rounded border-border text-brand focus:ring-brand mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-ink">InstaShop (Live menu ordering)</div>
                    <div className="text-xs text-ink-500">Walk-in ordering, pay from wallet, QR guest checkout.</div>
                  </div>
                </label>
                {form.instashop_enabled && (
                  <div className="mt-3 ml-8">
                    <Field label="InstaShop operated by" hint="Who manages the live menu and fulfilment?">
                      <select data-testid="sd-instashop-operator" value={form.instashop_operator || "school"} onChange={e => set("instashop_operator", e.target.value)} className="input">
                        <option value="school">School-operated</option>
                        <option value="vendor">Vendor-operated</option>
                      </select>
                    </Field>
                  </div>
                )}
              </div>
            </div>
          </Section>

          {/* Slot Timings */}
          <Section title="Meal slot timings (RFID validation)" icon={Clock}>
            <div className="text-xs text-ink-500 mb-3">Configure time windows for each meal slot. The RFID terminal validates students can only collect meals during their subscribed slot's time window.</div>
            <div className="space-y-3">
              {Object.entries(form.slot_timings || {}).map(([slot, timing]) => (
                <div key={slot} className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-ink w-24 capitalize">{slot}</span>
                  <input data-testid={`sd-slot-${slot}`} value={timing} onChange={e => setForm(f => ({ ...f, slot_timings: { ...f.slot_timings, [slot]: e.target.value } }))}
                    placeholder="HH:MM-HH:MM" className="input flex-1" />
                </div>
              ))}
              <button type="button" onClick={() => {
                const name = window.prompt("New slot name (e.g. dinner):");
                if (name?.trim()) setForm(f => ({ ...f, slot_timings: { ...f.slot_timings, [name.trim().toLowerCase()]: "18:00-20:00" } }));
              }} className="text-xs font-semibold text-brand hover:text-brand-700">+ Add slot</button>
            </div>
          </Section>

          {/* Academic Terms */}
          <Section title="Academic terms" icon={Clock}>
            <div className="text-xs text-ink-500 mb-3">Terms shown when an admin creates a "Term" meal plan. Add, rename or remove to match this school's academic calendar.</div>
            <div className="space-y-2" data-testid="sd-terms-list">
              {(form.terms || []).map((t, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    data-testid={`sd-term-${idx}`}
                    value={t}
                    onChange={e => setForm(f => ({ ...f, terms: f.terms.map((x, i) => i === idx ? e.target.value : x) }))}
                    placeholder="e.g. Term I"
                    className="input flex-1"
                  />
                  <button type="button" onClick={() => setForm(f => ({ ...f, terms: f.terms.filter((_, i) => i !== idx) }))} className="px-2 py-1 text-xs font-semibold text-red-600 hover:text-red-800">Remove</button>
                </div>
              ))}
              <button type="button" onClick={() => setForm(f => ({ ...f, terms: [...(f.terms || []), ""] }))} className="text-xs font-semibold text-brand hover:text-brand-700" data-testid="sd-add-term">+ Add term</button>
            </div>
          </Section>

          {/* Principal */}
          <Section title="Principal" icon={Phone}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name"><input data-testid="sd-prin-name" value={form.principal_name} onChange={e => set("principal_name", e.target.value)} className="input" /></Field>
              <Field label="Phone"><input data-testid="sd-prin-phone" value={form.principal_phone} onChange={e => set("principal_phone", e.target.value)} className="input" /></Field>
            </div>
            <Field label="Email"><input data-testid="sd-prin-email" value={form.principal_email} onChange={e => set("principal_email", e.target.value)} className="input" /></Field>
          </Section>

          {/* Admin contact */}
          <Section title="Primary admin contact" icon={Mail}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name"><input data-testid="sd-adm-name" value={form.admin_contact_name} onChange={e => set("admin_contact_name", e.target.value)} className="input" /></Field>
              <Field label="Phone"><input data-testid="sd-adm-phone" value={form.admin_contact_phone} onChange={e => set("admin_contact_phone", e.target.value)} className="input" /></Field>
            </div>
            <Field label="Email" hint="Invoices and weekly reports go to this address by default"><input data-testid="sd-adm-email" value={form.admin_contact_email} onChange={e => set("admin_contact_email", e.target.value)} className="input" /></Field>
          </Section>

          {/* Escalation */}
          <Section title="Escalation contacts" icon={Phone}>
            <div className="space-y-2">
              {(form.escalation_contacts || []).map((c, idx) => (
                <div key={idx} className="bg-white rounded-2xl border border-border p-3" data-testid={`esc-row-${idx}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={c.name} onChange={e => setEsc(idx, "name", e.target.value)} placeholder="Name" className="input text-sm" />
                    <input value={c.role} onChange={e => setEsc(idx, "role", e.target.value)} placeholder="Role (e.g., Operations)" className="input text-sm" />
                    <input value={c.phone} onChange={e => setEsc(idx, "phone", e.target.value)} placeholder="+91 …" className="input text-sm" />
                    <input value={c.email} onChange={e => setEsc(idx, "email", e.target.value)} placeholder="email@…" className="input text-sm" />
                  </div>
                  <div className="text-right mt-1">
                    <button onClick={() => rmEsc(idx)} className="text-[11px] text-red-600 hover:underline">Remove</button>
                  </div>
                </div>
              ))}
              <button onClick={addEsc} data-testid="add-esc" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-cream-200 text-ink-800 text-xs font-bold hover:bg-cream-300"><Plus size={12}/> Add escalation contact</button>
            </div>
          </Section>

          {/* Billing model */}
          <Section title="Billing model" icon={Receipt}>
            <div className="text-xs text-ink-500 mb-2">Locked once invoices are generated. Pick the most natural fit for this tenant.</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Model type">
                <select data-testid="sd-bm-type" value={form.billing_model?.type || "fixed"} onChange={e => setBM("type", e.target.value)} className="input">
                  <option value="fixed">Fixed monthly fee</option>
                  <option value="transactions">Per transaction</option>
                  <option value="value">% of order value</option>
                  <option value="users">Per active user</option>
                </select>
              </Field>
              <Field label={form.billing_model?.type === "value" ? "Rate (%)" : form.billing_model?.type === "users" ? "₹ / user / month" : form.billing_model?.type === "transactions" ? "₹ / order" : "Additional fixed (₹)"}>
                <input data-testid="sd-bm-rate" type="number" min={0} value={form.billing_model?.rate ?? 0} onChange={e => setBM("rate", e.target.value)} className="input" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Base monthly fee (₹)" hint="Always added — covers minimum platform cost">
                <input data-testid="sd-bm-base" type="number" min={0} value={form.billing_model?.base_monthly ?? 0} onChange={e => setBM("base_monthly", e.target.value)} className="input" />
              </Field>
              <Field label="Notes"><input value={form.billing_model?.notes || ""} onChange={e => setBM("notes", e.target.value)} placeholder="Trial period, special pricing…" className="input" /></Field>
            </div>
          </Section>

          <div className="bg-cream/95 backdrop-blur border-t border-border -mx-6 px-6 py-4 flex justify-end gap-2 sticky bottom-0">
            <button onClick={onClose} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
            <button data-testid="sd-save" onClick={save} disabled={busy}
              className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>} Save
            </button>
          </div>
        </div>
        <style>{`.input{width:100%;border-radius:12px;border:1px solid var(--border);padding:.5rem .75rem;background:#fff}.input:focus{outline:none;border-color:var(--brand);box-shadow:0 0 0 3px rgba(209,45,45,.15)}`}</style>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-500 inline-flex items-center gap-1.5 mb-3">{Icon && <Icon size={12}/>} {title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function LinkRow({ label, url, icon: Icon, testid, onCopy }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-3 flex items-center gap-3" data-testid={testid}>
      <Icon size={16} className="text-brand flex-shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold text-ink-700">{label}</div>
        <a href={url} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-ink-500 hover:text-brand truncate block">{url}</a>
      </div>
      <button onClick={() => onCopy(url)} className="w-7 h-7 rounded-full hover:bg-cream-200 inline-flex items-center justify-center text-ink-500" title="Copy"><Copy size={12}/></button>
      <a href={url} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-full hover:bg-cream-200 inline-flex items-center justify-center text-ink-500" title="Open"><ExternalLink size={12}/></a>
    </div>
  );
}

function InvoicesDrawer({ school, onClose }) {
  const [invs, setInvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [period, setPeriod] = useState({
    period_start: new Date(new Date().setDate(1)).toISOString().slice(0, 10),
    period_end: new Date().toISOString().slice(0, 10),
    gst_pct: 18, due_in_days: 15, notes: "",
  });
  const [paymentFor, setPaymentFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/platform/schools/${school.slug}/invoices`);
      setInvs(data.invoices || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [school.slug]);

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/platform/schools/${school.slug}/invoices`, period);
      toast.success("Invoice generated"); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setGenerating(false); }
  };

  const setF = (k, v) => setPeriod(p => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-cream w-full max-w-3xl ml-auto h-full flex flex-col" onClick={e => e.stopPropagation()} data-testid="invoices-drawer">
        <div className="flex-shrink-0 bg-white border-b border-border px-6 py-4 flex items-start justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-purple-700">Platform invoices</div>
            <div className="font-heading text-2xl font-extrabold text-ink mt-1">{school.name}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-cream-200 inline-flex items-center justify-center"><X size={16}/></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="bg-white rounded-2xl border border-border p-4">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-500 mb-3">Generate new invoice</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Field label="Period start"><input data-testid="inv-from" type="date" value={period.period_start} onChange={e => setF("period_start", e.target.value)} className="input" /></Field>
              <Field label="Period end"><input data-testid="inv-to" type="date" value={period.period_end} onChange={e => setF("period_end", e.target.value)} className="input" /></Field>
              <Field label="GST %"><input data-testid="inv-gst" type="number" value={period.gst_pct} onChange={e => setF("gst_pct", Number(e.target.value) || 0)} className="input" /></Field>
              <Field label="Due in days"><input data-testid="inv-due" type="number" value={period.due_in_days} onChange={e => setF("due_in_days", Number(e.target.value) || 0)} className="input" /></Field>
            </div>
            <Field label="Notes (optional)"><input data-testid="inv-notes" value={period.notes} onChange={e => setF("notes", e.target.value)} className="input" /></Field>
            <div className="text-right mt-3">
              <button data-testid="inv-generate" onClick={generate} disabled={generating}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-purple-600 text-white font-semibold disabled:opacity-60">
                {generating ? <Loader2 size={14} className="animate-spin"/> : <FileText size={14}/>} Generate invoice
              </button>
            </div>
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-500 mb-2">History</div>
            {loading ? <div className="text-ink-500 text-sm">Loading…</div> : invs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-border p-8 text-center text-ink-500 text-sm">No invoices yet — generate the first one above.</div>
            ) : (
              <div className="space-y-2">
                {invs.map(inv => (
                  <InvoiceRow key={inv.id} inv={inv} onPay={() => setPaymentFor(inv)} onChanged={load} />
                ))}
              </div>
            )}
          </div>
        </div>
        <style>{`.input{width:100%;border-radius:12px;border:1px solid var(--border);padding:.5rem .75rem;background:#fff}.input:focus{outline:none;border-color:var(--brand);box-shadow:0 0 0 3px rgba(209,45,45,.15)}`}</style>
        {paymentFor && <RecordPaymentModal invoice={paymentFor} onClose={() => setPaymentFor(null)} onRecorded={() => { setPaymentFor(null); load(); }} />}
      </div>
    </div>
  );
}

function InvoiceRow({ inv, onPay, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const tone = {
    issued: "bg-amber-100 text-amber-900",
    partial: "bg-blue-100 text-blue-900",
    paid: "bg-emerald-100 text-emerald-900",
    overdue: "bg-red-100 text-red-900",
    void: "bg-ink-100 text-ink-700",
    draft: "bg-cream-300 text-ink-700",
  }[inv.status] || "bg-cream-200";
  const outstanding = Math.max(0, (inv.total || 0) - (inv.paid_amount || 0));
  return (
    <div className="bg-white rounded-2xl border border-border" data-testid={`invoice-row-${inv.id}`}>
      <div className="p-4 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-mono font-bold text-sm">{inv.invoice_no}</div>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${tone}`}>{inv.status}</span>
          </div>
          <div className="text-[11px] text-ink-500 mt-0.5">{inv.period_start} → {inv.period_end} · due {inv.due_date}</div>
        </div>
        <div className="text-right">
          <div className="font-heading text-lg font-extrabold text-ink">{inrMoney(inv.total)}</div>
          {outstanding > 0 && <div className="text-[10px] text-red-600 font-bold">{inrMoney(outstanding)} outstanding</div>}
          {(inv.paid_amount || 0) > 0 && <div className="text-[10px] text-emerald-700">{inrMoney(inv.paid_amount)} paid</div>}
        </div>
        <div className="flex flex-col gap-1">
          {inv.status !== "paid" && inv.status !== "void" && (
            <button data-testid={`pay-${inv.id}`} onClick={onPay} className="px-3 py-1 rounded-md text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700">Record payment</button>
          )}
          <button onClick={() => setExpanded(e => !e)} className="px-3 py-1 rounded-md text-xs font-bold bg-cream-200 text-ink-700 hover:bg-cream-300">{expanded ? "Hide" : "Detail"}</button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-border p-4 bg-cream-100">
          <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500 mb-1">Line items</div>
          <table className="w-full text-xs">
            <tbody>
              {(inv.line_items || []).map((li, idx) => (
                <tr key={idx}><td>{li.label}</td><td className="text-right font-mono">{inrMoney(li.amount)}</td></tr>
              ))}
              <tr><td className="pt-2">Subtotal</td><td className="pt-2 text-right font-mono">{inrMoney(inv.subtotal)}</td></tr>
              <tr><td>GST ({inv.gst_pct}%)</td><td className="text-right font-mono">{inrMoney(inv.gst_amount)}</td></tr>
              <tr className="font-bold"><td>Total</td><td className="text-right">{inrMoney(inv.total)}</td></tr>
            </tbody>
          </table>
          {(inv.payments || []).length > 0 && (
            <>
              <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500 mt-3 mb-1">Payments received</div>
              <ul className="text-xs space-y-1">
                {inv.payments.map(p => (
                  <li key={p.id} className="flex justify-between">
                    <span>{p.received_on} · {p.method.toUpperCase()} · {p.ref || "—"}</span>
                    <span className="font-bold">{inrMoney(p.amount)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RecordPaymentModal({ invoice, onClose, onRecorded }) {
  const outstanding = Math.max(0, (invoice.total || 0) - (invoice.paid_amount || 0));
  const [form, setForm] = useState({
    amount: outstanding, method: "bank_transfer", ref: "",
    received_on: new Date().toISOString().slice(0, 10), note: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (Number(form.amount) <= 0) return toast.error("Amount must be positive");
    setBusy(true);
    try {
      await api.post(`/platform/invoices/${invoice.id}/payments`, {
        ...form, amount: Number(form.amount),
      });
      toast.success("Payment recorded");
      onRecorded();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-[60] flex items-center justify-center px-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl p-7 w-full max-w-md" data-testid="record-payment-modal">
        <div className="font-heading text-2xl font-bold text-ink">Record payment</div>
        <div className="text-xs text-ink-500 mt-1">Invoice {invoice.invoice_no} · outstanding <b>{inrMoney(outstanding)}</b></div>
        <div className="space-y-3 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount (₹) *"><input data-testid="rp-amount" type="number" min={0} max={outstanding} value={form.amount} onChange={e => set("amount", e.target.value)} className="input" /></Field>
            <Field label="Method">
              <select data-testid="rp-method" value={form.method} onChange={e => set("method", e.target.value)} className="input">
                <option value="bank_transfer">Bank transfer</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="cash">Cash</option>
                <option value="adjustment">Adjustment</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Received on"><input data-testid="rp-date" type="date" value={form.received_on} onChange={e => set("received_on", e.target.value)} className="input" /></Field>
            <Field label="Reference / UTR"><input data-testid="rp-ref" value={form.ref} onChange={e => set("ref", e.target.value)} className="input" placeholder="NEFT-…" /></Field>
          </div>
          <Field label="Note"><input data-testid="rp-note" value={form.note} onChange={e => set("note", e.target.value)} className="input" /></Field>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
          <button data-testid="rp-submit" onClick={submit} disabled={busy}
            className="px-5 py-2.5 rounded-full bg-emerald-600 text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>} Record
          </button>
        </div>
        <style>{`.input{width:100%;border-radius:12px;border:1px solid var(--border);padding:.5rem .75rem;background:#fff}.input:focus{outline:none;border-color:var(--brand);box-shadow:0 0 0 3px rgba(209,45,45,.15)}`}</style>
      </div>
    </div>
  );
}

function InviteAdminModal({ school, onClose, onInvited }) {
  const [form, setForm] = useState({ email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.email) return toast.error("Email required");
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/schools/${school.slug}/admins/invite`, form);
      toast.success(data.promoted ? "Existing user re-attached as admin" : `Invited · default password: grubpe123`);
      onInvited();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={`Invite admin · ${school.name}`} testid="invite-admin-modal">
      <div className="space-y-3">
        <Field label="Email *"><input data-testid="ia-email" autoFocus value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="input" placeholder="admin@school.com" /></Field>
        <Field label="Name"><input data-testid="ia-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="input" placeholder="School Admin" /></Field>
        <div className="text-xs text-ink-500">Default password <code className="font-mono bg-cream-200 px-1.5 rounded">grubpe123</code> — they should rotate on first login.</div>
      </div>
      <ModalActions onClose={onClose} onSubmit={submit} busy={busy} submitLabel="Send invite" testid="ia-submit" />
    </Modal>
  );
}

function BillingModal({ billing, onClose }) {
  const b = billing.billing;
  const s = billing.stats;
  return (
    <Modal onClose={onClose} title={`Billing · ${billing.school.name}`} testid="billing-modal">
      <div className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="text-xs font-bold uppercase text-amber-900 tracking-wider">Estimated bill ({billing.window_days} days)</div>
          <div className="font-heading text-4xl font-extrabold text-ink mt-1">{inrMoney(b.estimated_bill_inr)}</div>
          <div className="text-[11px] text-ink-500 mt-1">Base <b>{inrMoney(b.base_monthly)}</b>/month + <b>{inrMoney(b.per_order_rate)}</b>/order × {s.orders_30d} orders</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Mini label="Orders" value={s.orders_30d} />
          <Mini label="Revenue" value={inrMoney(s.revenue_30d)} />
          <Mini label="Users" value={s.users} />
          <Mini label="Vendors" value={s.vendors} />
        </div>
      </div>
      <div className="mt-5 text-right">
        <button onClick={onClose} className="px-5 py-2.5 rounded-full bg-ink text-white font-semibold">Close</button>
      </div>
    </Modal>
  );
}

// -------------------------------------------------
function Modal({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4 overflow-y-auto py-6" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl p-7 w-full max-w-xl my-auto" data-testid={testid}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-2xl font-bold text-ink">{title}</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-cream-200 inline-flex items-center justify-center"><X size={16}/></button>
        </div>
        {children}
      </div>
      <style>{`.input{width:100%;border-radius:12px;border:1px solid var(--border);padding:.5rem .75rem;background:#fff}.input:focus{outline:none;border-color:var(--brand);box-shadow:0 0 0 3px rgba(209,45,45,.15)}`}</style>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-wider text-ink-500">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10px] text-ink-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function ModalActions({ onClose, onSubmit, busy, submitLabel, testid }) {
  return (
    <div className="mt-6 flex justify-end gap-2">
      <button onClick={onClose} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
      <button data-testid={testid} disabled={busy} onClick={onSubmit}
        className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2">
        {busy ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>} {submitLabel}
      </button>
    </div>
  );
}
